export type PlanId = 'launch' | 'pilot' | 'copilot' | 'autopilot'

export type Plan = {
  id: PlanId
  tier: string
  name: string
  price: string
  purpose: string
  popular?: boolean
  headerClass: string
  bandClass: string
  subtleText: string
  buttonClass: string
  accentText: string
}

// true renders "Yes", false renders a dash, strings render as-is.
export type FeatureValue = boolean | string

export type Feature = {
  name: string
  values: Record<PlanId, FeatureValue>
}

export type FeatureCategory = {
  title: string
  footnote?: string
  features: Feature[]
}

export const plans: Plan[] = [
  {
    id: 'launch',
    tier: '72-Hour Launch',
    name: 'Launch',
    price: '$315',
    purpose: 'Get online fast. Custom website built & launched in 3 days, with up to 25 changes a month done in minutes.',
    headerClass: 'bg-brand-light text-brand-black',
    bandClass: 'bg-brand-light text-brand-black',
    subtleText: 'text-brand-gray',
    buttonClass: 'bg-brand-light text-brand-black hover:bg-[#dcdcdc]',
    accentText: 'text-brand-gray',
  },
  {
    id: 'pilot',
    tier: 'Entry',
    name: 'Pilot',
    price: '$615',
    purpose: 'Get the foundation right. Site, local SEO, phone and AI support.',
    headerClass: 'bg-brand-gray text-white',
    bandClass: 'bg-brand-gray text-white',
    subtleText: 'text-brand-light',
    buttonClass: 'bg-brand-gray text-white hover:bg-[#5a5a5a]',
    accentText: 'text-brand-gray',
  },
  {
    id: 'copilot',
    tier: 'Starter',
    name: 'Co-Pilot',
    price: '$1,235',
    purpose: 'Start driving demand. Paid ads, social and content added on top.',
    headerClass: 'bg-brand-black text-white',
    bandClass: 'bg-brand-black text-white',
    subtleText: 'text-brand-light',
    buttonClass: 'bg-brand-black text-white hover:bg-[#262626]',
    accentText: 'text-brand-black',
  },
  {
    id: 'autopilot',
    tier: 'Growth',
    name: 'Autopilot',
    price: '$2,780',
    purpose: 'Never miss a lead. Dedicated AdROI line, estimates, full pipelines.',
    popular: true,
    headerClass: 'bg-brand-red text-white',
    bandClass: 'bg-brand-red text-white',
    subtleText: 'text-brand-tint',
    buttonClass: 'bg-brand-red text-white hover:bg-brand-red-dark',
    accentText: 'text-brand-red',
  },
]

export const onboardingFee = '$997'

const all = { launch: true, pilot: true, copilot: true, autopilot: true }
const fromPilot = { launch: false, pilot: true, copilot: true, autopilot: true }
const fromCopilot = { launch: false, pilot: false, copilot: true, autopilot: true }
const autopilotOnly = { launch: false, pilot: false, copilot: false, autopilot: true }

export const featureCategories: FeatureCategory[] = [
  {
    title: 'Website',
    footnote:
      'Every site is professionally designed, built & launched within 3 days. After launch, send us any change — it’s done in minutes. Hosting stays active for as long as your plan is active.',
    features: [
      { name: 'Custom website built & launched in 72 hours', values: all },
      { name: 'Mobile-responsive design, hosting & SSL', values: all },
      { name: 'Website changes per month (all your sites combined)', values: { launch: 'Up to 25', pilot: 'Up to 50', copilot: 'Up to 100', autopilot: 'Up to 200' } },
      { name: 'Changes done within minutes', values: all },
      { name: 'Add unlimited pages', values: all },
      { name: 'Request changes by email, text, or Slack', values: all },
      { name: 'Custom landing pages for campaigns', values: all },
      { name: 'Daily backups', values: all },
      { name: 'Version control — roll back anytime', values: all },
      { name: 'Lead capture forms & surveys', values: all },
      { name: 'ADA accessibility & speed optimization', values: fromPilot },
      { name: 'E-commerce & online payments', values: fromCopilot },
    ],
  },
  {
    title: 'Get Found — Local SEO',
    features: [
      { name: 'Google Business Profile setup & optimization', values: fromPilot },
      { name: 'Business listings & citations', values: { launch: false, pilot: '50', copilot: '100', autopilot: '200' } },
      { name: 'On-page SEO for every page', values: fromPilot },
      { name: 'Tracked local keywords', values: { launch: false, pilot: '10', copilot: '25', autopilot: '50' } },
      { name: 'Google Business posts per month', values: { launch: false, pilot: '4', copilot: '8', autopilot: '12' } },
      { name: 'Local rank tracking (geo-grid)', values: fromCopilot },
      { name: 'Backlink building', values: { launch: false, pilot: false, copilot: false, autopilot: '5 / mo' } },
      { name: 'Competitor SEO monitoring', values: autopilotOnly },
    ],
  },
  {
    title: 'Lead Generation — Paid Ads (PPC)',
    features: [
      { name: 'Google Ads management', values: fromCopilot },
      { name: 'Meta (Facebook & Instagram) ads', values: fromCopilot },
      { name: 'Managed ad spend (billed separately)', values: { launch: false, pilot: false, copilot: 'Up to $5k', autopilot: 'Up to $15k' } },
      { name: 'Campaigns running at once', values: { launch: false, pilot: false, copilot: '2', autopilot: '5' } },
      { name: 'Ad creative & copywriting', values: fromCopilot },
      { name: 'Retargeting campaigns', values: autopilotOnly },
      { name: 'Google Local Services Ads', values: autopilotOnly },
      { name: 'Call tracking on every campaign', values: fromCopilot },
      { name: 'Monthly A/B testing', values: autopilotOnly },
    ],
  },
  {
    title: 'Content',
    features: [
      { name: 'Blog articles per month', values: { launch: false, pilot: false, copilot: '2', autopilot: '4' } },
      { name: 'Email newsletters per month', values: { launch: false, pilot: false, copilot: '1', autopilot: '2' } },
      { name: 'Short-form video scripts & edits', values: { launch: false, pilot: false, copilot: false, autopilot: '2' } },
    ],
  },
  {
    title: 'Social Media',
    features: [
      { name: 'Social profiles managed', values: { launch: false, pilot: '2', copilot: '3', autopilot: '5' } },
      { name: 'Posts per month', values: { launch: false, pilot: false, copilot: '8', autopilot: '16' } },
      { name: 'Branded graphics & captions', values: fromCopilot },
      { name: 'Social inbox (all DMs in one place)', values: fromPilot },
      { name: 'Comment & DM replies', values: autopilotOnly },
      { name: 'Social scheduling & planner', values: fromPilot },
    ],
  },
  {
    title: 'Email & SMS Marketing',
    features: [
      { name: 'Email & SMS broadcasts', values: fromPilot },
      { name: 'Pre-built nurture sequences', values: { launch: false, pilot: false, copilot: '3', autopilot: '10' } },
      { name: 'Custom automation workflows', values: { launch: false, pilot: false, copilot: 'Basic', autopilot: 'Advanced' } },
      { name: 'Birthday & re-engagement campaigns', values: fromCopilot },
      { name: 'List segmentation & tagging', values: fromPilot },
      { name: 'Monthly campaign reporting', values: fromCopilot },
    ],
  },
  {
    title: 'Phone System',
    features: [
      { name: 'Business phone system + mobile app', values: all },
      { name: 'Local business phone numbers', values: { launch: '1', pilot: '1', copilot: '2', autopilot: '3' } },
      { name: 'Dedicated AdROI line', values: autopilotOnly },
      { name: 'Missed-call text-back', values: all },
      { name: 'Two-way business texting', values: all },
      { name: 'Call recording', values: all },
      { name: 'Call forwarding & routing', values: all },
      { name: 'Business hours & after-hours rules', values: all },
      { name: 'Voicemail drop', values: fromPilot },
      { name: 'IVR phone menu', values: fromCopilot },
      { name: 'Call whisper & caller ID', values: fromCopilot },
      { name: 'Team Seats Included', values: { launch: '3', pilot: '5', copilot: '10', autopilot: '20' } },
      { name: 'Included minutes per month', values: { launch: '250', pilot: '500', copilot: '1,000', autopilot: '2,500' } },
      { name: 'Included texts per month', values: { launch: '250', pilot: '500', copilot: '1,000', autopilot: '2,500' } },
      { name: 'Toll-free number', values: autopilotOnly },
    ],
  },
  {
    title: 'AI Phone Attendant',
    features: [
      { name: 'AI answers every call, 24/7', values: all },
      { name: 'Books appointments on your calendar', values: all },
      { name: 'Answers FAQs in your brand voice', values: all },
      { name: 'Qualifies & tags new leads', values: fromPilot },
      { name: 'Transfers hot calls to your team', values: fromPilot },
      { name: 'Call summaries by text & email', values: fromPilot },
    ],
  },
  {
    title: 'AI Support — Chat and Follow-Up',
    features: [
      { name: 'AI support agent (website chat)', values: all },
      { name: 'Appointment reminders', values: all },
      { name: 'AI replies to texts & DMs', values: fromCopilot },
      { name: 'Automatic lead follow-up', values: fromCopilot },
      { name: 'No-show & reschedule recovery', values: autopilotOnly },
    ],
  },
  {
    title: 'Reviews & Reputation',
    features: [
      { name: 'Review request automation', values: fromPilot },
      { name: 'Google review widget for your site', values: fromPilot },
      { name: 'Negative review alerts', values: fromPilot },
      { name: 'AI review responses', values: fromPilot },
      { name: 'Video testimonials', values: autopilotOnly },
      { name: 'Multi-location review monitoring', values: autopilotOnly },
    ],
  },
  {
    title: 'Sales & Payments',
    features: [
      { name: 'Online booking calendar', values: all },
      { name: 'Payment processing', values: fromPilot },
      { name: 'Product & service catalog', values: fromPilot },
      { name: 'Invoicing & text-to-pay', values: fromCopilot },
      { name: 'Coupons & promotions', values: fromCopilot },
      { name: 'Estimates & quotes', values: autopilotOnly },
      { name: 'Proposals & e-signatures', values: autopilotOnly },
      { name: 'Memberships & subscriptions', values: autopilotOnly },
    ],
  },
  {
    title: 'Customer Database (CRM)',
    features: [
      { name: 'CRM & contact management — 1Dash.Pro', values: all },
      { name: 'Unified conversation inbox', values: all },
      { name: 'Task & follow-up reminders', values: all },
      { name: 'Import & export contacts', values: all },
      { name: 'Sales pipelines', values: { launch: '1', pilot: '1', copilot: '3', autopilot: 'Unlimited' } },
      { name: 'Custom fields & tags', values: fromPilot },
      { name: 'Team permissions', values: fromCopilot },
      { name: 'Live ROI dashboard', values: fromCopilot },
      { name: 'Lead scoring', values: autopilotOnly },
    ],
  },
  {
    title: 'Included Usage',
    features: [
      {
        name: 'Monthly AI & messaging credits included',
        values: { launch: '$5/month', pilot: '$15/month', copilot: '$30/month', autopilot: '$60/month' },
      },
    ],
  },
  {
    title: 'Reporting',
    features: [
      { name: 'Monthly performance report', values: fromPilot },
      { name: 'Advanced analytics & attribution', values: autopilotOnly },
    ],
  },
]
